class CategoriesCarousel {
  constructor(element) {
    this.element = element;
    this.container = element.querySelector('.gg-categories-carousel__container');
    this.track = element.querySelector('[data-slider-track]');
    this.prevButton = element.querySelector('[data-slider-prev]');
    this.nextButton = element.querySelector('[data-slider-next]');
    this.slides = Array.from(this.track.children);
    this.currentIndex = 0;
    this.slidesPerView = this.getSlidesPerView();
    this.isMobile = window.innerWidth < 768;
    this.slide = this.slide.bind(this);
    this.scrollbars = null;
    this.mousedown = false;
    this.startX = 0;
    this.scrollPos = 0;
    
    this.init();
  }

  init() {
    if (!this.track || this.slides.length <= this.slidesPerView) return;

    if (!this.isMobile) {
      this.initializeDesktop();
    } else {
      this.initializeMobile();
    }
    
    window.addEventListener('resize', this.handleResize.bind(this));
  }

  initializeDesktop() {
    // Initialize OverlayScrollbars
    if (window.OverlayScrollbarsGlobal) {
      this.scrollbars = window.OverlayScrollbarsGlobal.OverlayScrollbars({
        target: this.element,
        elements: {
          viewport: this.container
        },
        options: {
          scrollbars: {
            theme: 'os-theme-dark',
            autoHide: 'never',
            dragScroll: true
          }
        }
      }, {});
    }

    this.updateTrack();
    this.updateButtons();
    this.addEventListeners();
    
    // Add grabbable class to container
    this.container.classList.add('is-grabbable');

    // Add mouse event listeners for dragging
    this.container.addEventListener('mousedown', this.handleMousedown.bind(this));
    this.container.addEventListener('mouseup', this.handleMouseup.bind(this));
    this.container.addEventListener('mouseleave', this.handleMouseup.bind(this));
    this.container.addEventListener('mousemove', this.handleMousemove.bind(this));
  }

  initializeMobile() {
    if (this.scrollbars) {
      this.scrollbars.destroy();
    }

    // Reset any desktop-specific styles
    this.track.style.transform = '';
    this.currentIndex = 0;
    
    // Remove desktop event listeners
    if (this.prevButton) {
      this.prevButton.removeEventListener('click', this.slide);
    }
    if (this.nextButton) {
      this.nextButton.removeEventListener('click', this.slide);
    }

    // Remove mouse event listeners
    this.container.removeEventListener('mousedown', this.handleMousedown);
    this.container.removeEventListener('mouseup', this.handleMouseup);
    this.container.removeEventListener('mouseleave', this.handleMouseup);
    this.container.removeEventListener('mousemove', this.handleMousemove);
  }

  handleMousedown(evt) {
    this.mousedown = true;
    this.startX = evt.pageX - this.container.getBoundingClientRect().left;
    this.scrollPos = this.container.scrollLeft;
    this.container.classList.add('is-grabbing');
  }

  handleMouseup() {
    this.mousedown = false;
    this.container.classList.remove('is-grabbing');
    
    // Update button states based on scroll position
    this.updateButtons();
  }

  handleMousemove(evt) {
    if (!this.mousedown) return;
    evt.preventDefault();

    const x = evt.pageX - this.container.getBoundingClientRect().left;
    this.container.scrollLeft = this.scrollPos - (x - this.startX) * 2;
  }

  handleResize() {
    const wasMobile = this.isMobile;
    this.isMobile = window.innerWidth < 768;
    
    // Only reinitialize if we're switching between mobile and desktop
    if (wasMobile !== this.isMobile) {
      if (this.isMobile) {
        this.initializeMobile();
      } else {
        this.slidesPerView = this.getSlidesPerView();
        this.initializeDesktop();
      }
    } else if (!this.isMobile) {
      // Update desktop behavior if we're not on mobile
      const newSlidesPerView = this.getSlidesPerView();
      if (newSlidesPerView !== this.slidesPerView) {
        this.slidesPerView = newSlidesPerView;
        this.currentIndex = 0;
        this.updateTrack();
        this.updateButtons();
      }
    }
  }

  getSlidesPerView() {
    const width = window.innerWidth;
    if (width >= 1280) {
      return parseFloat(this.element.dataset.desktopSlides || 8.25);
    } else if (width >= 992) {
      return parseFloat(this.element.dataset.tabletSlides || 6.25);
    } else if (width >= 600) {
      return parseFloat(this.element.dataset.smallTabletSlides || 4.25);
    } else if (width >= 480) {
      return parseFloat(this.element.dataset.mobileSlides || 3.25);
    }
    return parseFloat(this.element.dataset.smallMobileSlides || 2.25);
  }

  addEventListeners() {
    if (this.prevButton) {
      this.prevButton.addEventListener('click', (e) => {
        // Check if the click was on the tap-target or its child
        const target = e.target.closest('.tap-target');
        if (target) {
          this.slide('prev');
        }
      });
    }
    if (this.nextButton) {
      this.nextButton.addEventListener('click', (e) => {
        // Check if the click was on the tap-target or its child
        const target = e.target.closest('.tap-target');
        if (target) {
          this.slide('next');
        }
      });
    }
  }

  slide(direction) {
    const scrollAmount = this.container.clientWidth * 0.8; // Scroll by 80% of the visible width
    const currentScroll = this.container.scrollLeft;
    
    if (direction === 'prev') {
      this.container.scrollTo({
        left: Math.max(0, currentScroll - scrollAmount),
        behavior: 'smooth'
      });
    } else {
      const maxScroll = this.container.scrollWidth - this.container.clientWidth;
      this.container.scrollTo({
        left: Math.min(maxScroll, currentScroll + scrollAmount),
        behavior: 'smooth'
      });
    }
    
    // Update button states after a short delay to allow for smooth scrolling
    setTimeout(() => {
      this.updateButtons();
    }, 300);
  }

  updateTrack() {
    const slideWidth = 100 / this.slidesPerView;
    const offset = -this.currentIndex * slideWidth;
    this.track.style.transform = `translateX(${offset}%)`;
  }

  updateButtons() {
    const scrollLeft = this.container.scrollLeft;
    const scrollWidth = this.container.scrollWidth;
    const clientWidth = this.container.clientWidth;
    
    if (this.prevButton) {
      this.prevButton.disabled = scrollLeft <= 0;
    }
    if (this.nextButton) {
      this.nextButton.disabled = scrollLeft + clientWidth >= scrollWidth;
    }
  }
}

// Initialize all carousels on the page
document.addEventListener('DOMContentLoaded', () => {
  document.querySelectorAll('[data-slider]').forEach(element => {
    new CategoriesCarousel(element);
  });
}); 